// Agency Accounts — frontend data contract (v1).
//
// The Account layer sits above brands (accounts / account_members /
// brand_payment_methods — see Brand-Dashboard/docs/AGENCY_FEATURE_SPEC.md §3).
// These types mirror the DB rows; useAccount.ts loads them with real Supabase
// queries. Keep in sync with the migration (20260625000003_agency_accounts_schema.sql).

import type { BrandConfig, BrandRole, ExistingBrandAccess } from './types'

export type AccountType = 'brand' | 'agency'
// owner   — one per account (accounts.owner_user_id). Untouchable by the team UI.
// co_owner— owner-equivalent EXCEPT ownership itself: billing and agency
//           settings included. Only a principal (owner/co-owner) may grant it.
// admin   — the agency's people and brands, no money.
// member  — works inside assigned brands only, as their admin.
// viewer  — read-only: sees the agency and its assigned brands, changes nothing
//           (the fan-out gives them brand 'viewer', not 'admin').
export type AccountRole = 'owner' | 'co_owner' | 'admin' | 'member' | 'viewer'
export type AccountPlanTier = 'free' | 'brand' | 'agency'
export type AccountPlanStatus = 'active' | 'trialing' | 'canceled' | 'past_due'

export interface Account {
  id: string
  type: AccountType
  name: string
  owner_user_id: string
  whop_subscription_id?: string | null
  plan_tier?: AccountPlanTier
  plan_status: AccountPlanStatus
  // Agency profile (captured at onboarding, editable in Agency Settings). Agencies
  // have no brand_configs row, so these live on the account. null for solo brands.
  logo_url?: string | null
  website?: string | null
  // description is kept in the data layer but its UI is commented out for now.
  description?: string | null
  // Agencies have no brand_configs row to carry an onboarding flag, so it lives
  // on the account. Brand accounts: true once backfilled (existing brands are done).
  onboarding_completed?: boolean
}

// A brand as shown in the agency roster + switcher — a light summary, not the
// full BrandConfig (which loads only once a brand is the active context).
export interface AgencyBrand {
  id: string
  brand_name: string
  logo_url?: string | null
  category?: string
}

/** A brand in the agency's roster, as seen by an owner/admin. `assigned` is the
 *  caller's OWN reach — false means the brand exists but they have no
 *  brand_user_access row for it, so the lobby shows it inert rather than hiding
 *  it (hiding it is how an admin ends up creating a duplicate). Carries no
 *  credential: every gate downstream still reads brand_user_access. */
export interface AgencyBrandRosterEntry extends AgencyBrand {
  assigned: boolean
}

// ── Agency team (account-level members with per-brand scoping) ──────────────
// The account-level sibling of TeamMember/Invitation in types.ts. A member holds
// an ACCOUNT role (owner/admin/member) plus a brand scope: either all_brands
// (every brand, including ones added later) or an explicit list of per-brand
// grants. The worker fans that scope out into brand_user_access rows, which is
// what every existing gate and RLS policy actually reads.

/** One brand a member may work in, and their role there. */
export interface MemberBrandGrant {
  brand_id: string
  role: BrandRole
}

/** A row of the agency Team roster (GET /api/agency/team/members). */
export interface AccountMember {
  id: string
  user_id: string
  email: string | null
  role: AccountRole
  all_brands: boolean
  /** Brand role applied to every brand when all_brands. Null = derive from the
   *  account role (owner/admin -> admin, member -> editor). */
  all_brands_role: BrandRole | null
  /** Explicit grants — only meaningful when all_brands is false. */
  brands: MemberBrandGrant[]
  /** What this person can already reach in the agency's brands, and who granted
   *  it. Read from brand_user_access, so it reflects EFFECT rather than intent:
   *  a 'brand' source means the agency would be overriding that brand's own
   *  grant. Drives the "already on this brand" annotations in the scope picker. */
  brand_access?: ExistingBrandAccess[]
  created_at: string
}

/** A pending account-level invite (GET /api/agency/team/invitations). */
export interface AccountInvitation {
  id: string
  email: string
  role: AccountRole
  all_brands: boolean
  all_brands_role: BrandRole | null
  brands: MemberBrandGrant[]
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  invited_by_email: string | null
  expires_at: string
  created_at: string
}

// ── Per-brand teams (the brand side of the same roster) ─────────────────────
// Everyone who can reach each brand, however they got in. Agency assignments are
// only half the picture: a brand can add people to its own team, and since the
// agency Team page can now create those directly, the agency needs to see them.
// Returned alongside the roster by GET /api/agency/team/members.

/** One person with access to one brand. */
export interface BrandTeamPerson {
  user_id: string
  email: string | null
  role: BrandRole
  /** The brand's protected owner. Nothing the agency assigns can change it. */
  is_owner: boolean
  /** 'agency' = fanned out from an account membership, managed on this page.
   *  'brand'  = created on that brand's own Team page; the agency layer shows it
   *  but never silently rewrites it. */
  source: 'brand' | 'agency'
  /** Set on an agency row overriding a brand-native grant: the role the brand
   *  had set, restored if the agency's grant is withdrawn. */
  native_role: BrandRole | null
  /** Also on the agency roster — the same person seen from the brand side. */
  account_member: boolean
}

/** A brand-native invitation that hasn't been accepted yet. */
export interface BrandTeamPendingInvite {
  id: string
  brand_id: string
  email: string
  role: BrandRole
  expires_at: string | null
}

/** One brand's people. Present for every brand in the agency, empty included. */
export interface BrandTeam {
  brand_id: string
  members: BrandTeamPerson[]
  pending: BrandTeamPendingInvite[]
}

/** Public invite details for the agency accept page. */
export interface AgencyInviteDetails {
  email: string
  role: AccountRole
  agency_name: string
  all_brands: boolean
  /** Names of the brands they're being given — concrete beats "an agency". */
  brand_names: string[]
  is_new_user: boolean
}

/** The scope half of an invite/update payload. */
export interface MemberScopeInput {
  all_brands: boolean
  all_brands_role?: BrandRole | null
  brands?: MemberBrandGrant[]
}

// Per-brand named card (§4). Card lives at the brand level; each brand is its
// own payment identity. Mirrors brand_payment_methods.
export interface PaymentMethod {
  id: string
  brand_id: string
  label: string   // human name shown in every picker/confirmation ("Acme Corp Amex")
  last4: string
  is_default: boolean
  exp?: string    // display-only; not stored in v1
}

export interface AccountData {
  account: Account
  brands: AgencyBrand[]
}

// Map a DB `accounts` row → Account.
export function accountFromRow(row: {
  id: string; type: string; name?: string | null; owner_user_id: string
  whop_subscription_id?: string | null; plan_tier?: string | null; plan_status?: string | null
  logo_url?: string | null; website?: string | null; description?: string | null
  onboarding_completed?: boolean | null
}): Account {
  return {
    id: row.id,
    type: (row.type as AccountType) ?? 'brand',
    name: row.name ?? '',
    owner_user_id: row.owner_user_id,
    whop_subscription_id: row.whop_subscription_id ?? null,
    plan_tier: (row.plan_tier as AccountPlanTier) ?? undefined,
    plan_status: (row.plan_status as AccountPlanStatus) ?? 'active',
    logo_url: row.logo_url ?? null,
    website: row.website ?? null,
    description: row.description ?? null,
    onboarding_completed: row.onboarding_completed ?? false,
  }
}

// Map a brand row → the light roster summary.
export function agencyBrandFromConfig(b: {
  id: string; brand_name: string; logo_url?: string | null; category?: string | null
}): AgencyBrand {
  return { id: b.id, brand_name: b.brand_name, logo_url: b.logo_url ?? null, category: b.category ?? undefined }
}

// Derive a solo 'brand' account from the loaded brand. Used as a real fallback
// when no `accounts` row resolves yet (e.g. a brand created before the backfill,
// or the brief rollout window before the worker writes account rows) so the app
// always has account context. NOT a mock — it reflects the brand's own state.
// `ownerUserId` reflects real ownership (from brand_user_access.is_owner) so
// owner-gated actions (e.g. "Upgrade to Agency") work for legacy brands the user
// owns; leave it '' for non-owners (brand-scoped team members).
export function soloAccountFromBrand(brand: BrandConfig, ownerUserId = ''): AccountData {
  const status = (brand.plan_status === 'cancelled' ? 'canceled' : brand.plan_status) as AccountPlanStatus
  return {
    account: {
      id: brand.account_id ?? 'acc_' + brand.id,
      type: 'brand',
      name: brand.brand_name,
      owner_user_id: ownerUserId,
      whop_subscription_id: brand.whop_subscription_id ?? null,
      plan_tier: undefined,
      plan_status: status ?? 'active',
      // Mirror the brand's onboarding state so a solo-fallback account never
      // makes App think onboarding is needed when the brand is already set up.
      onboarding_completed: brand.onboarding_completed ?? true,
    },
    brands: [agencyBrandFromConfig(brand)],
  }
}
